import { useState, useEffect, useCallback, useRef, forwardRef } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { HugeiconsIcon } from "@hugeicons/react";
import { Search01Icon } from "@hugeicons/core-free-icons";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  useCompanyDetail,
  useAddCompany,
  useUpdateCompany,
  useCargoCompanyNames,
  useCargoCompanyDetail,
} from "../hooks/useCompanies";
import {
  BusinessStatus,
  BUSINESS_STATUS_MAP,
} from "../types/company.types";
import type {
  CargoCompanyNameItem,
  BusinessStatusType,
  BusinessRegistrationType,
  DispatchCompanyDetail,
  CargoCompanyDetail,
} from "../types/company.types";

// --- Schema & Constants ---

const companySchema = z.object({
  companyName: z.string().min(1, "업체명은 필수입니다"),
  representativeName: z.string().optional(),
  businessRegNum: z.string().optional(),
  businessRegistrationType: z.enum(["SOLE", "CORP"]).optional(),
  businessType: z.string().optional(),
  businessItem: z.string().optional(),
  phoneNum: z.string().optional(),
  phoneNumSub: z.string().optional(),
  email: z.string().optional(),
  addressHq: z.string().optional(),
  managerName: z.string().optional(),
  managerPhoneNum: z.string().optional(),
  creditLimit: z.coerce.number().optional(),
  departmentInCharge: z.string().optional(),
  faxNum: z.string().optional(),
});

type CompanyFormValues = z.infer<typeof companySchema>;

const BUSINESS_REG_TYPES: { value: BusinessRegistrationType; label: string }[] = [
  { value: "SOLE", label: "개인" },
  { value: "CORP", label: "법인" },
];

// --- Helpers ---

function toFormValues(
  source: DispatchCompanyDetail | CargoCompanyDetail,
): CompanyFormValues {
  return {
    companyName: source.companyName ?? "",
    representativeName: source.representativeName ?? "",
    businessRegNum: source.businessRegNum ?? "",
    businessRegistrationType: source.businessRegistrationType ?? undefined,
    businessType: source.businessType ?? "",
    businessItem: source.businessItem ?? "",
    phoneNum: source.phoneNum ?? "",
    phoneNumSub: source.phoneNumSub ?? "",
    email: source.email ?? "",
    addressHq: source.addressHq ?? "",
    managerName: source.managerName ?? "",
    managerPhoneNum: source.managerPhoneNum ?? "",
    creditLimit: "creditLimit" in source ? (source.creditLimit ?? undefined) : undefined,
    departmentInCharge: source.departmentInCharge ?? "",
    faxNum: source.faxNum ?? "",
  };
}

// --- Sub-components ---

interface ToggleButtonGroupProps<T extends string | number> {
  label: string;
  options: readonly { value: T; label: string }[];
  value: T | undefined;
  onChange: (value: T | undefined) => void;
  allowDeselect?: boolean;
}

function ToggleButtonGroup<T extends string | number>({
  label,
  options,
  value,
  onChange,
  allowDeselect = false,
}: ToggleButtonGroupProps<T>) {
  return (
    <div>
      <label className="mb-1 block text-xs font-medium text-gray-700">
        {label}
      </label>
      <div className="flex gap-1">
        {options.map((opt) => {
          const selected = value === opt.value;
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() =>
                onChange(selected && allowDeselect ? undefined : opt.value)
              }
              className={`h-8 flex-1 rounded-md border text-xs font-medium transition-colors ${
                selected
                  ? "border-blue-600 bg-blue-600 text-white"
                  : "border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
              }`}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

const FormField = forwardRef<
  HTMLInputElement,
  { label: string } & React.InputHTMLAttributes<HTMLInputElement>
>(({ label, ...props }, ref) => (
  <div>
    <label className="mb-1 block text-xs font-medium text-gray-700">
      {label}
    </label>
    <input
      ref={ref}
      className="h-8 w-full rounded-md border border-gray-300 px-3 text-xs outline-none focus:border-blue-500"
      {...props}
    />
  </div>
));

const BUSINESS_STATUS_OPTIONS = Object.entries(BusinessStatus).map(
  ([, value]) => ({
    value: value as BusinessStatusType,
    label: BUSINESS_STATUS_MAP[value as BusinessStatusType].label,
  }),
);

// --- Main Component ---

interface CompanyFormProps {
  mode: "add" | "edit";
  companyGuid: string | null;
}

export function CompanyForm({ mode, companyGuid }: CompanyFormProps) {
  const { data: detail, isLoading: isDetailLoading } =
    useCompanyDetail(companyGuid);
  const addMutation = useAddCompany();
  const updateMutation = useUpdateCompany();

  // 추가 모드: 업체 검색
  const [cargoSearchStr, setCargoSearchStr] = useState("");
  const [showCargoResults, setShowCargoResults] = useState(false);
  const [selectedCargoGuid, setSelectedCargoGuid] = useState<string | null>(null);
  const resultsRef = useRef<HTMLDivElement>(null);
  const { data: cargoNames } = useCargoCompanyNames(cargoSearchStr);
  const { data: cargoDetail } = useCargoCompanyDetail(selectedCargoGuid);

  // 수정 모드: 상태 관리
  const [isPause, setIsPause] = useState(false);
  const [businessStatus, setBusinessStatus] = useState<BusinessStatusType>(
    BusinessStatus.ACTIVE,
  );

  const {
    register,
    handleSubmit,
    reset: resetForm,
    setValue,
    watch,
    formState: { errors },
  } = useForm<CompanyFormValues>({
    resolver: zodResolver(companySchema),
  });

  // 수정 모드: 상세 데이터 로드 시 폼 세팅
  useEffect(() => {
    if (mode === "edit" && detail) {
      resetForm(toFormValues(detail));
      setIsPause(detail.isPause);
      setBusinessStatus(detail.businessStatus);
    }
  }, [mode, detail, resetForm]);

  // 추가 모드: 화주 업체 선택 시 폼 세팅
  useEffect(() => {
    if (mode === "add" && cargoDetail) {
      resetForm(toFormValues(cargoDetail));
      setShowCargoResults(false);
    }
  }, [mode, cargoDetail, resetForm]);

  const handleCargoSearch = useCallback(() => {
    setShowCargoResults(true);
  }, []);

  const handleCargoSelect = useCallback((item: CargoCompanyNameItem) => {
    setSelectedCargoGuid(item.companyGuid);
    setCargoSearchStr(item.companyName);
    setShowCargoResults(false);
  }, []);

  // 결과 영역 외부 클릭 시 닫기
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (resultsRef.current && !resultsRef.current.contains(e.target as Node)) {
        setShowCargoResults(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const onSubmit = (data: CompanyFormValues) => {
    if (mode === "add") {
      addMutation.mutate(data);
    } else if (companyGuid) {
      updateMutation.mutate({
        companyGuid,
        ...data,
        isPause,
        businessStatus,
      });
    }
  };

  const isPending = addMutation.isPending || updateMutation.isPending;

  if (mode === "edit" && isDetailLoading) {
    return (
      <div className="flex flex-1 items-center justify-center text-xs text-gray-400">
        로딩 중...
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="flex flex-1 flex-col overflow-hidden"
    >
      <div className="flex-1 space-y-3 overflow-y-auto px-4 py-2">
        {/* 업체명 */}
        <div>
          <label className="mb-1 block text-xs font-medium text-gray-700">
            업체명 <span className="text-red-500">*</span>
          </label>
          {mode === "add" ? (
            <div className="relative" ref={resultsRef}>
              <div className="flex items-center gap-1">
                <input
                  type="text"
                  value={cargoSearchStr}
                  onChange={(e) => {
                    setCargoSearchStr(e.target.value);
                    setValue("companyName", e.target.value);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleCargoSearch();
                    }
                  }}
                  placeholder="업체명을 입력하고 조회"
                  className="h-8 flex-1 rounded-md border border-gray-300 px-3 text-xs outline-none focus:border-blue-500"
                />
                <Button
                  type="button"
                  size="sm"
                  onClick={handleCargoSearch}
                  className="h-8 bg-blue-600 px-3 text-xs font-bold hover:bg-blue-700"
                >
                  <HugeiconsIcon icon={Search01Icon} size={14} className="mr-1" />
                  조회
                </Button>
              </div>
              {showCargoResults && cargoNames && cargoNames.length > 0 && (
                <div className="absolute z-20 mt-1 max-h-48 w-full overflow-auto rounded-md border border-gray-200 bg-white shadow-lg">
                  {cargoNames.map((item) => (
                    <button
                      key={item.companyGuid}
                      type="button"
                      onClick={() => handleCargoSelect(item)}
                      className="flex w-full items-center px-3 py-2 text-left text-xs hover:bg-gray-50"
                    >
                      <span className="font-medium text-gray-900">
                        {item.companyName}
                      </span>
                      {item.regSource && (
                        <span className="ml-2 text-gray-400">
                          ({item.regSource})
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              )}
              {showCargoResults && cargoNames?.length === 0 && cargoSearchStr && (
                <div className="absolute z-20 mt-1 w-full rounded-md border border-gray-200 bg-white p-3 text-center text-xs text-gray-400 shadow-lg">
                  검색 결과가 없습니다
                </div>
              )}
            </div>
          ) : (
            <input
              type="text"
              {...register("companyName")}
              className="h-8 w-full rounded-md border border-gray-300 px-3 text-xs outline-none focus:border-blue-500"
            />
          )}
          {errors.companyName && (
            <p className="mt-1 text-xs text-red-500">
              {errors.companyName.message}
            </p>
          )}
        </div>

        {mode === "edit" && companyGuid && (
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-700">
              업체 GUID
            </label>
            <input
              type="text"
              value={companyGuid}
              readOnly
              onFocus={(e) => e.currentTarget.select()}
              className="h-8 w-full cursor-text select-all rounded-md border border-gray-300 bg-gray-100 px-3 font-mono text-xs text-gray-600 outline-none"
            />
          </div>
        )}

        <FormField label="대표자" {...register("representativeName")} />
        <FormField label="사업자번호" {...register("businessRegNum")} />

        <ToggleButtonGroup
          label="사업자유형"
          options={BUSINESS_REG_TYPES}
          value={watch("businessRegistrationType")}
          onChange={(v) => setValue("businessRegistrationType", v, { shouldDirty: true })}
          allowDeselect
        />

        <div className="grid grid-cols-2 gap-3">
          <FormField label="업태" {...register("businessType")} />
          <FormField label="종목" {...register("businessItem")} />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <FormField label="전화번호" {...register("phoneNum")} />
          <FormField label="보조 전화번호" {...register("phoneNumSub")} />
        </div>

        <FormField label="이메일" type="email" {...register("email")} />
        <FormField label="본사 주소" {...register("addressHq")} />

        <div className="grid grid-cols-2 gap-3">
          <FormField label="담당자명" {...register("managerName")} />
          <FormField label="담당자 전화번호" {...register("managerPhoneNum")} />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <FormField label="담당 부서" {...register("departmentInCharge")} />
          <FormField label="팩스번호" {...register("faxNum")} />
        </div>

        <FormField label="신용 한도" type="number" {...register("creditLimit")} />

        {mode === "edit" && (
          <div className="space-y-3 border-t border-gray-200 pt-3">
            <ToggleButtonGroup
              label="업체 상태"
              options={BUSINESS_STATUS_OPTIONS}
              value={businessStatus}
              onChange={(v) => {
                if (v !== undefined) setBusinessStatus(v);
              }}
            />
            <div className="flex items-center justify-between">
              <label className="text-xs font-medium text-gray-700">
                일시정지
              </label>
              <Switch checked={isPause} onCheckedChange={setIsPause} size="sm" />
            </div>
          </div>
        )}
      </div>

      <div className="flex gap-2 border-t border-gray-200 p-4">
        <Button
          type="submit"
          disabled={isPending}
          className="flex-1 bg-blue-600 text-sm font-bold hover:bg-blue-700"
        >
          {isPending ? "저장 중..." : "저장"}
        </Button>
      </div>
    </form>
  );
}
